<template>
    <div class="modal fade show hotel-modal" aria-labelledby="checkoutModalTitle">
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div class="modal-content">
                <div class="d-flex justify-content-between align-items-center gap-2 px-2 my-1">
                    <h1 class="modal-title-text mb-0">{{ modalTitle }}</h1>
                    <div class="d-flex align-items-center gap-1 flex-shrink-0">
                        <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
                    </div>
                </div>

                <hr class="mt-0">

                <Loader v-if="loading"/>

                <div v-if="!loading" class="modal-body">
                    <div class="row g-1">
                        <div class="col-lg-7">
                            <div class="detail-section">
                                <div class="section-title">Reservation Details</div>
                                <div class="row g-1">
                                    <div class="col-md-3 col-6">
                                        <div class="detail-label">Reservation No</div>
                                        <div class="detail-value">{{ textValue(reservation.reservation_no) }}</div>
                                    </div>
                                    <div class="col-md-3 col-6">
                                        <div class="detail-label">Status</div>
                                        <div class="detail-value">
                                            <span class="badge" :class="statusClass">{{ statusLabel }}</span>
                                        </div>
                                    </div>
                                    <div class="col-md-3 col-6">
                                        <div class="detail-label">Guest</div>
                                        <div class="detail-value">{{ textValue(reservation.guest_name) }}</div>
                                    </div>
                                  <div v-if="reservation.notes" class="col-md-3 col-6">
                                    <div class="detail-label">Notes</div>
                                    <div class="detail-value notes-value">{{ reservation.notes }}</div>
                                  </div>
                                    <div class="col-md-3 col-6">
                                        <div class="detail-label">Mobile No</div>
                                        <div class="detail-value">{{ textValue(reservation.guest_mobile) }}</div>
                                    </div>
                                    <div class="col-md-3 col-6">
                                        <div class="detail-label">Check-in</div>
                                        <div class="detail-value">{{ textValue(reservation.check_in_formatted) }}</div>
                                    </div>
                                    <div class="col-md-3 col-6">
                                        <div class="detail-label">Check-out</div>
                                        <div class="detail-value">{{ textValue(reservation.check_out_formatted) }}</div>
                                    </div>
                                    <div class="col-md-3 col-6">
                                        <div class="detail-label">Nights</div>
                                        <div class="detail-value">{{ textValue(reservation.nights) }}</div>
                                    </div>
                                    <div class="col-md-3 col-6">
                                        <div class="detail-label">Payment</div>
                                        <div class="detail-value">
                                            <span class="badge" :class="paymentClass">{{ paymentLabel }}</span>
                                        </div>
                                    </div>
                                    <div v-if="reservation.booked_by" class="col-md-3 col-6">
                                        <div class="detail-label">Booked By</div>
                                        <div class="detail-value">{{ reservation.booked_by.name }}</div>
                                    </div>
                                    <div v-if="reservation.referrer" class="col-md-3 col-6">
                                        <div class="detail-label">Referrer</div>
                                        <div class="detail-value">{{ reservation.referrer.name }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-lg-5">
                            <div v-if="roomRows.length || !isHousekeeping" class="detail-section">
                                <div class="row g-1 summar-card-details" v-if="!isHousekeeping">
                                    <div class="col-4">
                                        <div class="summary-card summary-total">
                                            <span class="summary-label">Estimate Bill</span>
                                            <span class="summary-value">{{ estimateBillLabel }}</span>
                                        </div>
                                    </div>
                                    <div class="col-4">
                                        <div class="summary-card summary-paid">
                                            <span class="summary-label">Paid</span>
                                            <span class="summary-value">{{ commaFormat(reservation.paid) }}</span>
                                        </div>
                                    </div>
                                    <div class="col-4">
                                        <div class="summary-card summary-due">
                                            <span class="summary-label">Due</span>
                                            <span class="summary-value">{{ commaFormat(reservation.due) }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="roomRows.length" class="table-responsive room-card">
                                    <table class="table table-sm table-bordered detail-table mb-0">
                                        <thead>
                                        <tr>
                                            <th v-if="!isHousekeeping"></th>
                                            <th>Room</th>
                                            <th v-if="!isHousekeeping" class="text-end">Rate</th>
                                            <th class="text-end">Nights</th>
                                            <th v-if="!isHousekeeping" class="text-end">Amount</th>
                                            <th>Status</th>
                                            <th v-if="!isHousekeeping">Expected Checkout</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="room in roomRows" :key="room.service_resource_id">
                                            <td v-if="!isHousekeeping">
                                                <input
                                                    type="checkbox"
                                                    :disabled="room.status === 'checked_out'"
                                                    :checked="isRoomSelected(room)"
                                                    @change="toggleRoomSelection(room)"
                                                >
                                            </td>
                                            <td>{{ room.name }}</td>
                                            <td v-if="!isHousekeeping" class="text-end">{{ commaFormat(room.rate) }}</td>
                                            <td class="text-end">{{ roomNights(room) }}</td>
                                            <td v-if="!isHousekeeping" class="text-end">{{ commaFormat(room.amount) }}</td>
                                            <td>
                                                <span class="badge" :class="room.status === 'checked_out' ? 'bg-light-secondary' : 'bg-light-success'">
                                                    {{ room.status === 'checked_out' ? 'Checked Out' : 'Occupied' }}
                                                </span>
                                            </td>
                                            <td v-if="!isHousekeeping">
                                                <input
                                                    v-if="room.status !== 'checked_out'"
                                                    type="datetime-local"
                                                    class="form-control form-control-sm"
                                                    :value="expectedCheckoutInput(room)"
                                                    @change="onExpectedCheckoutChange(room, $event.target.value)"
                                                >
                                                <span v-else>{{ textValue(room.expected_check_out_formatted) }}</span>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div v-if="!isHousekeeping && billAdjustPreviews.length" class="d-flex justify-content-end mt-1">
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-danger"
                                        @click="openBillAdjustModal"
                                    >
                                        <i class="fa fa-exclamation-triangle me-1"></i>Bill Adjust ({{ billAdjustPreviews.length }})
                                    </button>
                                </div>
                            </div>

                            <div v-if="paymentRows.length && !isHousekeeping && false" class="detail-section">
                                <div class="section-title">Payment History</div>
                                <div class="table-responsive payment-scroll">
                                    <table class="table table-sm table-bordered detail-table mb-0">
                                        <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Voucher</th>
                                            <th>Received In</th>
                                            <th>Reference</th>
                                            <th class="text-end">Amount</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="payment in paymentRows" :key="payment.id">
                                            <td>{{ textValue(payment.receipt_date) }}</td>
                                            <td>{{ textValue(payment.voucher_no) }}</td>
                                            <td>{{ textValue(payment.account_head_name) }}</td>
                                            <td>{{ textValue(payment.receipt_reference) }}</td>
                                            <td class="text-end">{{ commaFormat(payment.total_paid_amount) }}</td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div class="col-12">
                            <div v-if="hasChecklist" class="checklist">
                                <div class="checklist-title">Checkout Checklist</div>
                                <div class="checklist-grid">
                                    <div
                                        v-for="item in checklist"
                                        :key="item.note_master_id"
                                        class="checklist-item"
                                        :class="{ 'is-checked': item.checked }"
                                    >
                                        <label class="checklist-row">
                                            <input type="checkbox" v-model="item.checked" class="checklist-input">
                                            <span class="tick" :class="{ 'tick-on': item.checked }">
                                                <i class="fa fa-check"></i>
                                            </span>
                                            <span class="checklist-label">{{ item.label }}</span>
                                        </label>
                                        <textarea
                                            v-if="item.checked"
                                            v-model="item.description"
                                            class="form-control checklist-desc"
                                            rows="1"
                                            placeholder="Add a note (optional)"
                                        ></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="!isHousekeeping" class="col-12">
                            <div class="detail-section statement-embed">
                                <div class="statement-tabs">
                                    <button
                                        v-for="tab in statementTabs"
                                        :key="tab.id"
                                        type="button"
                                        class="statement-tab-btn"
                                        :class="{ 'is-active': activeStatementTab === tab.id }"
                                        @click="activeStatementTab = tab.id"
                                    >
                                        <i class="fa" :class="tab.icon"></i>
                                        {{ tab.title }}
                                    </button>
                                </div>

                                <HotelStatementTab
                                    v-if="activeStatementTab === 'statement'"
                                    :service="statementService"
                                    :receiptGenerals="statementReceiptGenerals"
                                    :total="statementTotal"
                                    :patientDetails="statementPatientDetails"
                                    :reopen-checkout-id="reservationId"
                                    @onSettled="onStatementSettled"
                                />
                                <HotelBillTab
                                    v-if="activeStatementTab === 'ipd-bill'"
                                    :reopen-checkout-id="reservationId"
                                />
                                <HotelMoneyReceiptTab
                                    v-if="activeStatementTab === 'money-receipts'"
                                    :patientDetails="statementPatientDetails"
                                    :reopen-checkout-id="reservationId"
                                />
                            </div>
                        </div>
                    </div>

                    <div v-if="!isHousekeeping && isFullSelection && !isSettled" class="alert-note alert-note-danger">
                        This reservation is not settled yet. Settle the bill from Detail Billing before checking out.
                    </div>
                    <div v-if="selectedRoomIds.length" class="alert-note">
                        {{ selectionSummary }}
                    </div>
                    <div v-if="isHousekeeping && !selectedRoomIds.length" class="alert-note">
                        Current status: {{ currentStatusLabel }}. Update the checklist and save a new status, or select a room above to check it out.
                    </div>

                    <div class="d-flex flex-wrap align-items-end justify-content-end gap-1 mt-2 modal-actions">
                        <div>
                            <div class="section-title">Check-out Date</div>
                            <input type="datetime-local" v-model="checkoutDate" class="form-control form-control-sm">
                        </div>
                        <div class="d-flex gap-1">
                            <button type="button" class="btn btn-outline-secondary" @click="closeModal">Cancel</button>
                            <button v-if="!isHousekeeping" type="button" :disabled="printing" class="btn btn-outline-primary" @click="goToPrintDetails">Details</button>
                            <button v-if="!isHousekeeping" type="button" :disabled="printing" class="btn btn-outline-primary" @click="goToPrint">Summary</button>
                            <button
                                v-if="isHousekeeping && !isEdit"
                                type="button"
                                class="btn btn-warning"
                                :disabled="saving"
                                @click="onConfirm('on_claim')"
                            >
                                On Claim
                            </button>
                            <button
                                v-if="isHousekeeping && !isEdit"
                                type="button"
                                class="btn btn-success"
                                :disabled="saving"
                                @click="onConfirm('ready_for_checkout')"
                            >
                                Ready for Checkout
                            </button>
                            <button
                                v-if="isEdit"
                                type="button"
                                class="btn btn-primary"
                                :disabled="saving"
                                @click="onConfirm(editStatus)"
                            >
                                Update
                            </button>
                            <button
                                v-if="!isHousekeeping"
                                type="button"
                                class="btn btn-primary"
                                :disabled="!checkoutDate || saving || !selectedRoomIds.length"
                                @click="onConfirm('released')"
                            >
                                Check Out Selected Room(s)
                            </button>
                            <button
                                v-if="!isHousekeeping && !needsBookingRelease"
                                type="button"
                                class="btn btn-primary"
                                :disabled="!checkoutDate || saving || !selectedRoomIds.length || (isFullSelection && !isSettled)"
                                @click="onConfirm('released')"
                            >
                                {{ confirmButtonLabel }}
                            </button>
                            <button
                                v-if="needsBookingRelease"
                                type="button"
                                class="btn btn-primary"
                                :disabled="saving"
                                @click="onReleaseBooking"
                            >
                                Release Booking
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <BillAdjustPreviewModal
            v-if="showBillAdjustModal"
            :previews="billAdjustPreviews"
            :reservation-id="reservationId"
            @onClose="showBillAdjustModal = false"
            @onConfirmed="onBillAdjustConfirmed"
        />
    </div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent';
import HotelStatementTab from '@/components/molecule/company/hotel/hotel-bill-statement/tabs/HotelStatementTab.vue';
import HotelBillTab from '@/components/molecule/company/hotel/hotel-bill-statement/tabs/HotelBillTab.vue';
import HotelMoneyReceiptTab from '@/components/molecule/company/hotel/hotel-bill-statement/tabs/HotelMoneyReceiptTab.vue';
import BillAdjustPreviewModal from '@/components/molecule/company/hotel/hotel-reservation/BillAdjustPreviewModal.vue';
import handleHospital from '@/services/modules/hospital';
import handleHotel from '@/services/modules/hotel/hotel';
import handleCompany from '@/services/modules/company';
import handleBarcode from '@/services/modules/barcode';
import useDate from '@/services/utils/day';
import figureFormatter from '@/services/utils/figureFormatter';
import hotelStatementHelper from '@/services/utils/pdf/hotelStatementHelper';
import hotelStatementDetailsMulti from '@/services/utils/pdf/hotelStatementDetailsMulti';
import { useStore } from 'vuex';

const CHECKED_OUT_STATUS = 'checked_out';

const props = defineProps({
    reservationId: {
        type: Number,
        required: true,
    },
    isHousekeeping: {
        type: Boolean,
        default: false,
    },
    editStatus: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['onClose', 'onCheckedOut']);

const $route = useRoute();
const $router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { notesByType, fetchIpdStatement, fetchIpdStatementDetailsMulti } = handleHospital();
const {
    fetchSingleHotelReservation,
    checkoutHotelReservation,
    checkoutHotelReservationRoom,
    updateHotelReservationRoomExpectedCheckout,
} = handleHotel();
const { fetchCompanyInfo } = handleCompany();
const { fetchBarcode } = handleBarcode();
const { generatePdf } = hotelStatementHelper();
const { exportToPDF } = hotelStatementDetailsMulti();
const store = useStore();
const { currentDateTimeForInput, formatDate } = useDate();
const { commaFormat } = figureFormatter();

const statementTabs = [
    { id: 'statement', title: 'Statements', icon: 'fa-file-invoice-dollar' },
    { id: 'ipd-bill', title: 'Bill', icon: 'fa-receipt' },
    { id: 'money-receipts', title: 'Money Receipts', icon: 'fa-money-bill-wave' },
];

const loading = ref(false);
const saving = ref(false);
const reservation = ref({});
const checklist = ref([]);
const checkoutDate = ref(currentDateTimeForInput());
const activeStatementTab = ref('statement');
const statementPatientDetails = ref({});
const statementService = ref([]);
const statementReceiptGenerals = ref([]);
const statementTotal = ref({});
const selectedRoomIds = ref([]);
const expectedCheckoutOverrides = ref({});
const showBillAdjustModal = ref(false);
const printing = ref(false);
const companyInfo = ref({});

const hasChecklist = computed(() => checklist.value.length > 0);

const isEdit = computed(() => !!props.editStatus);

const isSettled = computed(() => reservation.value.settlement_status === 'settled');

const modalTitle = computed(() => {
    if (isEdit.value) return 'Update Housekeeping Status';
    if (props.isHousekeeping) return 'Housekeeping Status';
    return 'Check Out';
});

const statusLabels = {
    ready_for_checkout: 'Ready for Checkout',
    on_claim: 'On Claim',
    checked_in: 'Checked In',
};

const currentStatusLabel = computed(() => {
    const status = reservation.value.registration_status;
    if (!status) return '—';
    if (!statusLabels[status]) return status;
    return statusLabels[status];
});

const statusBadges = {
    pending: 'bg-light-warning',
    confirmed: 'bg-light-info',
    checked_in: 'bg-light-success',
    ready_for_checkout: 'bg-light-success',
    on_claim: 'bg-light-warning',
    released: 'bg-light-secondary',
    completed: 'bg-light-secondary',
    cancelled: 'bg-light-danger',
};

const paymentBadges = {
    paid: 'bg-light-success',
    partial: 'bg-light-warning',
    unpaid: 'bg-light-danger',
};

const humanize = (value) => {
    if (!value) return '-';
    return value.split('_').map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
};

const textValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
};

const statusLabel = computed(() => humanize(reservation.value.registration_status));

const paymentLabel = computed(() => humanize(reservation.value.payment_status));

const statusClass = computed(() => {
    const badge = statusBadges[reservation.value.registration_status];
    if (!badge) return 'bg-light-secondary';
    return badge;
});

const paymentClass = computed(() => {
    const badge = paymentBadges[reservation.value.payment_status];
    if (!badge) return 'bg-light-secondary';
    return badge;
});

const estimateBillLabel = computed(() => {
    if (!(Number(reservation.value.return_amount) > 0)) return commaFormat(reservation.value.total);
    return `${commaFormat(reservation.value.gross_bill)} - ${commaFormat(reservation.value.return_amount)} = ${commaFormat(reservation.value.total)}`;
});

const nightCount = computed(() => {
    if (!reservation.value.nights) return 1;
    return Number(reservation.value.nights);
});

const roomNights = (room) => {
    if (!room.nights) return nightCount.value;
    return Number(room.nights);
};

const roomRows = computed(() => {
    if (!reservation.value.rooms) return [];
    return reservation.value.rooms.map((room) => {
        return Object.assign({}, room, { amount: Number(room.rate) * roomNights(room) });
    });
});

const occupiedRoomIds = computed(() => {
    return roomRows.value
        .filter(room => room.status !== CHECKED_OUT_STATUS)
        .map(room => room.service_resource_id);
});

const isFullSelection = computed(() => {
    if (!occupiedRoomIds.value.length) return false;
    if (selectedRoomIds.value.length !== occupiedRoomIds.value.length) return false;
    return occupiedRoomIds.value.every(id => selectedRoomIds.value.includes(id));
});

const confirmButtonLabel = computed(() => {
    if (isFullSelection.value) return 'Confirm Check Out';
    return 'Check Out Selected Room(s)';
});

const selectionSummary = computed(() => {
    if (isFullSelection.value) return 'Confirm check out for this guest. The rooms will be released.';
    return `${selectedRoomIds.value.length} room(s) selected for checkout.`;
});

const needsBookingRelease = computed(() => {
    if (props.isHousekeeping) return false;
    if (reservation.value.registration_status === 'released') return false;
    if (!roomRows.value.length) return false;
    return occupiedRoomIds.value.length === 0;
});

const isRoomSelected = (room) => {
    return selectedRoomIds.value.includes(room.service_resource_id);
};

const toggleRoomSelection = (room) => {
    if (isRoomSelected(room)) {
        selectedRoomIds.value = selectedRoomIds.value.filter(id => id !== room.service_resource_id);
        return;
    }
    selectedRoomIds.value = selectedRoomIds.value.concat([room.service_resource_id]);
};

const expectedCheckoutInput = (room) => {
    if (expectedCheckoutOverrides.value[room.service_resource_id]) {
        return expectedCheckoutOverrides.value[room.service_resource_id];
    }
    if (!room.expected_check_out) return '';
    return room.expected_check_out;
};

const onExpectedCheckoutChange = (room, value) => {
    if (!value) return;
    expectedCheckoutOverrides.value = Object.assign({}, expectedCheckoutOverrides.value, {
        [room.service_resource_id]: value,
    });
    const payload = { company_id: $route.params.companyId, expected_check_out: value };
    updateHotelReservationRoomExpectedCheckout(payload, props.reservationId, room.service_resource_id).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        reservation.value = Object.assign({}, reservation.value, { bill_adjust_previews: res.data });
        getReservation(false);
    }).catch(() => {
        showError('Something went wrong');
    });
};

const billAdjustPreviews = computed(() => {
    if (!reservation.value.bill_adjust_previews) return [];
    return reservation.value.bill_adjust_previews;
});

const openBillAdjustModal = () => {
    if (!billAdjustPreviews.value.length) return;
    showBillAdjustModal.value = true;
};

const onBillAdjustConfirmed = async () => {
    await getReservation(false);
    await getStatement();
    if (billAdjustPreviews.value.length) return;
    showBillAdjustModal.value = false;
};

const paymentRows = computed(() => {
    if (!reservation.value.payments) return [];
    return reservation.value.payments;
});

const seedRoomSelection = (rooms) => {
    if (!rooms) return;
    if (props.isHousekeeping) {
        selectedRoomIds.value = [];
        return;
    }
    selectedRoomIds.value = rooms
        .filter(room => room.status !== CHECKED_OUT_STATUS)
        .map(room => room.service_resource_id);
};

const getReservation = (showLoader = true) => {
    if (showLoader) loading.value = true;
    const query = `?company_id=${$route.params.companyId}`;
    return fetchSingleHotelReservation(query, props.reservationId).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        reservation.value = res.data;
        seedRoomSelection(res.data.rooms);
        if (!res.data.check_out) return;
        checkoutDate.value = res.data.check_out;
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        if (showLoader) loading.value = false;
    });
};

const getStatement = () => {
    if (props.isHousekeeping) return Promise.resolve();
    const query = `/${props.reservationId}?company_id=${$route.params.companyId}&with_return=1`;
    return fetchIpdStatement(query).then(res => {
        if (!res.status) return;
        statementPatientDetails.value = res.data;
        statementService.value = res.service_data;
        statementReceiptGenerals.value = res.receipt_generals;
        statementTotal.value = res.total;
        statementTotal.value.settlement_discount = res.data.settlement_discount;
    });
};

const onStatementSettled = () => {
    getStatement();
    getReservation(false);
};

const setRegisterIdQuery = () => {
    if ($route.query.ipd_register_id === String(props.reservationId)) return;
    const routeQuery = Object.assign({}, $route.query, { ipd_register_id: props.reservationId });
    $router.replace({ path: $route.path, query: routeQuery }).catch(() => {});
};

const clearRegisterIdQuery = () => {
    if ($route.query.ipd_register_id !== String(props.reservationId)) return;
    const routeQuery = Object.assign({}, $route.query);
    delete routeQuery.ipd_register_id;
    $router.replace({ path: $route.path, query: routeQuery }).catch(() => {});
};

const savedChecklistMap = () => {
    const saved = reservation.value.checkout_checklist;
    if (!saved) return {};
    return saved.reduce((map, item) => {
        map[item.note_master_id] = item;
        return map;
    }, {});
};

const buildChecklistItem = (item, saved) => {
    const savedItem = saved[item.id];
    if (!savedItem) {
        return {
            note_master_id: item.id,
            label: item.name,
            checked: false,
            description: '',
        };
    }
    return {
        note_master_id: item.id,
        label: item.name,
        checked: true,
        description: savedItem.description,
    };
};

const getChecklist = () => {
    const query = `?company_id=${$route.params.companyId}`;
    return notesByType(query, 'checkout_checklist').then(res => {
        if (!res.status) return;
        const saved = savedChecklistMap();
        checklist.value = res.data.map(item => buildChecklistItem(item, saved));
    });
};

const buildCheckState = () => {
    return checklist.value
        .filter(item => item.checked)
        .map(item => ({
            note_master_id: item.note_master_id,
            label: item.label,
            checked: item.checked,
            description: item.description,
        }));
};

const closeModal = () => {
    emit('onClose');
};

const getCompanyInfo = async () => {
    if (companyInfo.value.id) return companyInfo.value;
    const res = await fetchCompanyInfo($route.params.companyId);
    companyInfo.value = res.data;
    return companyInfo.value;
};

const goToPrint = async () => {
    if (printing.value) return;
    printing.value = true;
    try {
        const company = await getCompanyInfo();
        await generatePdf(company, statementPatientDetails.value, statementService.value, statementReceiptGenerals.value, statementTotal.value);
    } catch (e) {
        showError('Something went wrong');
    } finally {
        printing.value = false;
    }
};

const goToPrintDetails = async () => {
    if (printing.value) return;
    printing.value = true;
    const userName = store.state.user.user.name;

    try {
        const company = await getCompanyInfo();
        const productIds = statementService.value.map(item => item.id);
        const detailsQuery = `?company_id=${$route.params.companyId}&product_ids=${productIds.join(',')}&with_return=1`;
        const detailsRes = await fetchIpdStatementDetailsMulti(props.reservationId, detailsQuery);

        if (! detailsRes.status) {
            showError('Something went wrong');
            return;
        }

        const ipdRegisterDetails = { ...detailsRes.data };
        const saleMastersGrouped = detailsRes.sale_masters_grouped;

        let qrcodeData = JSON.stringify({
            ref_no: ipdRegisterDetails.admission_no,
            ref_date: ipdRegisterDetails.admission_date,
            created_by: userName,
            created_date_time: formatDate(new Date(ipdRegisterDetails.created_at)),
        });
        const barCodeQuery = `?code=${ipdRegisterDetails.patient_serial_no}&qrcode=${qrcodeData}`;
        const barcodeRes = await fetchBarcode(barCodeQuery);

        if (! barcodeRes.status) {
            showError('Something went wrong');
            return;
        }

        const roomName = ipdRegisterDetails.service_resource_name;
        exportToPDF(company, ipdRegisterDetails, saleMastersGrouped, userName, barcodeRes.barcode, barcodeRes.qrcode, statementReceiptGenerals.value, statementTotal.value, roomName);
    } catch (e) {
        showError('Something went wrong');
    } finally {
        printing.value = false;
    }
};

const completionStatusFor = (status) => {
    if (status === 'released') return 'completed';
    return status;
};

const buildCheckoutPayload = (status) => {
    return {
        company_id: $route.params.companyId,
        reg_status: status,
        completion_status: completionStatusFor(status),
        check_out: checkoutDate.value,
        checkout_checklist: buildCheckState(),
    };
};

const checkoutSelectedRooms = (payload) => {
    const requests = selectedRoomIds.value.map(roomId => {
        return checkoutHotelReservationRoom(payload, props.reservationId, roomId);
    });
    return Promise.all(requests).then(results => {
        const failed = results.find(res => !res.status);
        if (failed) {
            showError(failed.message);
            return;
        }
        showSuccess(results[results.length - 1].message);
        emit('onCheckedOut');
    });
};

const checkoutWholeBooking = (payload) => {
    return checkoutHotelReservation(payload, props.reservationId).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        emit('onCheckedOut');
    });
};

const onReleaseBooking = () => {
    if (saving.value) return;
    if (!props.reservationId) return;
    const payload = buildCheckoutPayload('released');
    saving.value = true;
    checkoutWholeBooking(payload).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        saving.value = false;
    });
};

const isRoomCheckoutAction = (status) => {
    if (status !== 'released') return false;
    if (props.isHousekeeping) return true;
    return ! isFullSelection.value;
};

const runCheckoutSubmit = (status, payload) => {
    if (isRoomCheckoutAction(status)) return checkoutSelectedRooms(payload);
    return checkoutWholeBooking(payload);
};

const onConfirm = (status = 'released') => {
    if (!checkoutDate.value) return;
    if (!props.reservationId) return;
    if (saving.value) return;
    if (status === 'released' && !selectedRoomIds.value.length) return;
    if (status === 'released' && !props.isHousekeeping && isFullSelection.value && !isSettled.value) return;
    const payload = buildCheckoutPayload(status);
    saving.value = true;
    runCheckoutSubmit(status, payload).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        saving.value = false;
    });
};

onMounted(async () => {
    setRegisterIdQuery();
    await getReservation();
    await getChecklist();
    await getStatement();
});

onBeforeUnmount(() => {
    clearRegisterIdQuery();
});
</script>

<style scoped>
.hotel-modal {
    display: block;
    background: #1c1d1c33;
    overflow-y: auto;
}
.modal-title-text {
    font-size: 1.25rem;
    line-height: 1.3;
    word-break: break-word;
    min-width: 0;
}
@media (max-width: 575.98px) {
    .modal-title-text {
        font-size: 1.05rem;
    }
    .modal-body {
        padding: 0.5rem;
    }
}
.section-title {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #5e5873;
    margin-bottom: 0.3rem;
}
.detail-section {
    background: #f8f8f8;
    border: 1px solid #ebe9f1;
    border-radius: 0.4rem;
    padding: 0.45rem 0.55rem;
}
.detail-label {
    display: block;
    font-size: 0.63rem;
    line-height: 1.3;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #6e6b7b;
    opacity: 0.85;
}
.detail-value {
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.3;
    color: #384250;
    word-break: break-word;
}
.notes-value {
    max-height: 42px;
    overflow-y: auto;
    font-weight: 500;
}
@media (max-width: 991.98px) {
    .notes-value {
        max-height: 70px;
    }
}
.detail-table {
    min-width: 0;
    font-size: 0.76rem;
}
.detail-table th,
.detail-table td {
    padding: 0.25rem 0.4rem;
    white-space: nowrap;
}
.summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0.4rem 0.6rem;
    border-radius: 0.4rem;
    border: 1px solid transparent;
    height: 100%;
}
.summary-label {
    font-size: 0.6rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    line-height: 1.2;
    white-space: nowrap;
    opacity: 0.75;
    margin-bottom: 0.1rem;
}
.summary-value {
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.2;
}
.summary-total {
    background: #eef2f7;
    border-color: #dfe6ef;
    color: #384250;
}
.summary-paid {
    background: #e7f6ec;
    border-color: #cdeed8;
    color: #1a7f43;
}
.summary-due {
    background: #fdeceb;
    border-color: #f8d5d2;
    color: #c0392b;
}
@media (max-width: 767.98px) {
    .detail-section {
        padding: 0.5rem;
    }
    .summary-value {
        font-size: 0.85rem;
    }
    .summary-label {
        white-space: normal;
    }
    .summary-card {
        padding: 0.4rem;
    }
}
.modal-actions .btn {
    font-size: 0.85rem;
}
@media (max-width: 575.98px) {
    .modal-actions .btn {
        flex: 1 1 100%;
    }
}
.alert-note {
    padding: 0.4rem 0.6rem;
    border-radius: 0.4rem;
    background: #fff8e6;
    border: 1px solid #f5e2b3;
    color: #8a6d1a;
    font-size: 0.78rem;
}
.payment-scroll {
    max-height: 190px;
    overflow-x: auto;
    overflow-y: auto;
}
.alert-note-danger {
    background: #fdeaea;
    border-color: #f5c2c2;
    color: #a3251c;
}
.checklist {
    border: 1px solid #eceef1;
    border-radius: 0.4rem;
    padding: 0.4rem 0.5rem;
}
.checklist-title {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #5e5873;
    margin-bottom: 0.3rem;
}
.checklist-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.35rem;
    align-items: stretch;
}
@media (max-width: 991.98px) {
    .checklist-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
@media (max-width: 575.98px) {
    .checklist-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}
.checklist-item {
    display: flex;
    flex-direction: column;
    padding: 0.3rem 0.45rem;
    border: 1px solid #eceef1;
    border-radius: 0.5rem;
    background: #fbfcfd;
    transition: border-color 0.15s ease, background 0.15s ease;
}
.checklist-item.is-checked {
    border-color: #b7e0c4;
    background: #f2fbf5;
}
.checklist-row {
    display: flex;
    align-items: flex-start;
    gap: 0.45rem;
    cursor: pointer;
    margin-bottom: 0;
    flex-grow: 1;
}
.checklist-desc {
    margin-top: 0.3rem;
    width: 100%;
    padding: 0.2rem 0.4rem;
    font-size: 0.75rem;
    resize: vertical;
}
.modal-body {
    padding: 0.5rem 0.75rem 0.75rem;
}
.checklist-input {
    display: none;
}
.tick {
    flex-shrink: 0;
    margin-top: 1px;
    width: 17px;
    height: 17px;
    border-radius: 5px;
    border: 1.5px solid #d0d5dd;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    font-size: 0.7rem;
    transition: all 0.15s ease;
}
.tick-on {
    background: #1fa363;
    border-color: #1fa363;
    color: #ffffff;
}
.checklist-label {
    font-size: 0.78rem;
    color: #475467;
    line-height: 1.3;
    word-break: break-word;
}
.checklist-item.is-checked .checklist-label {
    color: #101828;
    font-weight: 600;
}

.statement-embed :deep(.card) {
    border: none;
    box-shadow: none;
    background: transparent;
    padding: 0;
    min-height: 0 !important;
}
.statement-embed :deep(.table) {
    font-size: 0.78rem;
    margin-bottom: 0.5rem;
}
.statement-embed :deep(.table th) {
    font-size: 0.63rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #6e6b7b;
    background: #f2f4f7;
    border-bottom: none;
    padding: 0.35rem 0.5rem;
}
.statement-embed :deep(.table td) {
    padding: 0.35rem 0.5rem;
    vertical-align: middle;
}
.statement-embed :deep(.bg-blue-light) {
    background: #eef2f7 !important;
    padding: 0.35rem 0.5rem;
    border-radius: 0.3rem;
}
.statement-embed :deep(.bg-blue-light td) {
    font-size: 0.72rem;
    font-weight: 700;
    color: #384250;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}
.statement-embed :deep(.return-row td) {
    background: #fdeceb;
    color: #a3251c;
}
.statement-embed :deep(.paid),
.statement-embed :deep(.due) {
    display: inline-flex;
    align-items: center;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    line-height: 1.2;
}
.statement-embed :deep(.paid) {
    background: #e6f6ee;
    color: #1a7a49;
}
.statement-embed :deep(.due) {
    background: #fdeceb;
    color: #a3251c;
}
.statement-embed :deep(.discount-adj) {
    font-size: 0.78rem;
    padding: 0.2rem 0.4rem;
    height: auto;
}
.statement-embed :deep(.btn-success) {
    font-size: 0.8rem;
    border-radius: 0.4rem;
}
.statement-embed :deep(.v-select) {
    font-size: 0.8rem;
}
.statement-embed :deep(.card-height) {
    min-height: 0;
}
.statement-embed :deep(.row.py-2) {
    padding-top: 0;
    padding-bottom: 0;
    align-items: flex-start !important;
}
.statement-embed :deep(.row.py-2 .col-md-6:first-child) {
    padding-top: 0.2rem;
}
.statement-embed :deep(.payment-scroll),
.statement-embed :deep(.table-responsive) + .table-responsive {
    max-height: 160px;
}
.statement-embed :deep(.mt-5) {
    margin-top: 0.75rem !important;
}
.statement-embed :deep(.offset-md-2) {
    margin-left: 0;
}
.statement-embed :deep(.card-body) {
    padding: 0.4rem 0.5rem;
}
.statement-embed :deep(.mt-2) {
    margin-top: 0.4rem !important;
}
.statement-embed :deep(.my-2) {
    margin-top: 0.4rem !important;
    margin-bottom: 0.4rem !important;
}
.statement-embed :deep(form) {
    margin-bottom: 0;
}
.room-card {
    padding-top: 2px;
}
.statement-embed :deep(.pagination) {
    margin-bottom: 0;
}
.statement-embed :deep(.h1) {
    font-size: 0.95rem;
    font-weight: 700;
}
.statement-embed :deep(.card.p-2) {
    padding: 0 !important;
}
.statement-tabs {
    display: inline-flex;
    gap: 0.2rem;
    padding: 0.2rem;
    margin-bottom: 0.7rem;
    background: #eef0f3;
    border-radius: 0.6rem;
}
.statement-tab-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    border: none;
    background: transparent;
    padding: 0.35rem 0.85rem;
    border-radius: 0.45rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: #6e6b7b;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.statement-tab-btn .fa {
    font-size: 0.72rem;
    opacity: 0.8;
}
.statement-tab-btn:hover {
    color: #384250;
}
.statement-tab-btn.is-active {
    background: #ffffff;
    color: #1a7a49;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);
}
.statement-tab-btn.is-active .fa {
    opacity: 1;
}
@media (max-width: 575.98px) {
    .statement-tabs {
        width: 100%;
    }
    .statement-tab-btn {
        flex: 1 1 0;
        justify-content: center;
        padding: 0.35rem 0.4rem;
    }
}
</style>
